import {MouseEvent, RefObject, useEffect, useMemo, useRef, useState} from "react";
import {Button, Col, Row, Spinner} from "react-bootstrap";
import {Link, useLocation, useNavigate, useParams} from "react-router";
import {useAuth} from "../../auth/AuthContext";
import {DocumentationApi} from "../../api/DocumentationApi";
import {ApiError} from "../../api/http";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {TocEntry, withHeadingIds} from "./documentToc";
import {DocumentationNotice} from "./DocumentationNotice";
import {TrademarkNotices, trademarksIn} from "../../components/TrademarkNotices";
import {docsPath, documentation as documentationData, GLOSSARY_PATH} from "../../structuredData";
import {docs, format} from "../../i18n";

const messages = docs.page;

// A product's documentation, with its contents in a pane on the left (above it, on narrow screens). Addressed by SKU,
// like the product pages; the API by product id, so the SKU is looked up among the documented products.
export function DocumentationPage() {
    const {sku = ''} = useParams();
    const {data: documentation, error, loading} = useAsync(async () => {
        const products = await DocumentationApi.list();
        const product = products.find(x => x.sku.toLowerCase() === sku.toLowerCase());
        if (!product) {
            throw new ApiError(404);
        }
        return await DocumentationApi.get(product.productId);
    }, [sku]);
    const document = useMemo(() => {
        if (!documentation) {
            return undefined;
        }
        const result = withHeadingIds(documentation.content);
        return { ...result, summary: result.summary || format(messages.summary, { product: documentation.productName }) };
    }, [documentation]);
    const content = useRef<HTMLDivElement>(null);
    const active = useActiveSection(content, document?.toc);
    const navigate = useNavigate();

    // Links to the site's own pages (the glossary) stay in the app rather than loading the site again.
    const followSiteLink = (e: MouseEvent<HTMLDivElement>) => {
        const link = (e.target as HTMLElement).closest('a');
        const href = link?.getAttribute('href') ?? '';
        if (link && /^\/(?!\/)/.test(href) && !link.target && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            navigate(href);
        }
    };

    // A link to a section (/docs/ASTREL#doc-filter): the content arrives after the browser looked for it.
    useEffect(() => {
        const id = decodeURIComponent(window.location.hash.substring(1));
        if (document && id) {
            window.document.getElementById(id)?.scrollIntoView();
        }
    }, [document]);

    return (
        <>
            <div className="mb-2"><Link to="/docs" className="small"><i className="bi bi-arrow-left me-1"></i>{messages.all}</Link></div>
            <ErrorAlert error={error} messages={{ 404: messages.noDocumentation }}/>
            {loading && !documentation && <LoadingSpinner/>}
            {documentation && document && (
                <>
                    <PageHeader title={format(messages.title, { product: documentation.productName })} description={document.summary} path={docsPath(documentation.sku)}
                                jsonLd={documentationData(documentation, document.summary)}>
                        <DownloadPdfButton productId={documentation.productId}/>
                    </PageHeader>
                    <DocumentationNotice/>
                    <Row className="g-4">
                        {document.toc.length > 0 && (
                            <Col lg={3}>
                                <Contents toc={document.toc} active={active}/>
                            </Col>
                        )}
                        <Col lg={document.toc.length > 0 ? 9 : 12}>
                            <div ref={content} className="rich-text docs-content" onClick={followSiteLink} dangerouslySetInnerHTML={{ __html: document.html }}/>
                            <TrademarkNotices marks={trademarksIn(documentation.content)} className="mt-5"/>
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
}

// On wide screens it stays in view beside the document; on narrow ones it's above it, behind a toggle.
function Contents({toc, active}: { toc: TocEntry[], active?: string }) {
    const [open, setOpen] = useState(false);
    const link = (entry: TocEntry) => (
        <a href={`#${entry.id}`} className={`docs-toc-link${entry.id === active ? ' active' : ''}`}
           aria-current={entry.id === active ? 'location' : undefined} onClick={() => setOpen(false)}>
            {entry.text}
        </a>
    );
    return (
        <nav aria-label={messages.contents} className="docs-toc">
            <Button variant="outline-secondary" size="sm" className="d-lg-none w-100 text-start" aria-expanded={open} aria-controls="docs-toc-list"
                    onClick={() => setOpen(x => !x)}>
                <i className={`bi bi-chevron-${open ? 'down' : 'right'} me-1`} aria-hidden="true"></i>{messages.contents}
            </Button>
            <div id="docs-toc-list" className={`collapse d-lg-block${open ? ' show' : ''}`}>
                <h2 className="h6 text-body-secondary text-uppercase small d-none d-lg-block mb-2">{messages.contents}</h2>
                <ul className="list-unstyled mb-0 mt-2 mt-lg-0">
                    {toc.map(entry => (
                        <li key={entry.id}>
                            {link(entry)}
                            {entry.children.length > 0 && (
                                <ul className="list-unstyled ms-3">
                                    {entry.children.map(child => <li key={child.id}>{link(child)}</li>)}
                                </ul>
                            )}
                        </li>
                    ))}
                </ul>
                <Link to={GLOSSARY_PATH} className="docs-toc-link mt-3"><i className="bi bi-book me-1" aria-hidden="true"></i>{messages.glossary}</Link>
            </div>
        </nav>
    );
}

// The section being read: the last heading scrolled past the top of the window (the first, before any).
function useActiveSection(content: RefObject<HTMLDivElement | null>, toc?: TocEntry[]): string | undefined {
    const [active, setActive] = useState<string>();
    useEffect(() => {
        const headings = [...(content.current?.querySelectorAll<HTMLElement>('h2[id], h3[id]') ?? [])];
        if (!headings.length || typeof IntersectionObserver === 'undefined') {
            return;
        }
        const update = () => {
            // Below the sticky navbar.
            const top = 96;
            const passed = headings.filter(x => x.getBoundingClientRect().top <= top);
            setActive((passed[passed.length - 1] ?? headings[0]).id);
        };
        const observer = new IntersectionObserver(update, { rootMargin: '-96px 0px 0px 0px', threshold: [0, 1] });
        headings.forEach(x => observer.observe(x));
        update();
        return () => observer.disconnect();
    }, [content, toc]);
    return active;
}

// Asks the API for a PDF marked with the user's name and a download ID, then downloads it. Signed out, it's a link to
// the login screen, which comes back here.
function DownloadPdfButton({productId}: { productId: string }) {
    const {user} = useAuth();
    const location = useLocation();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    if (!user) {
        const returnTo = location.pathname + location.search;
        return (
            <Link to={`/login?returnTo=${encodeURIComponent(returnTo)}`} className="btn btn-outline-primary">
                <i className="bi bi-box-arrow-in-right me-1" aria-hidden="true"></i>{messages.signInToDownload}
            </Link>
        );
    }

    const download = async () => {
        setBusy(true);
        setError(undefined);
        try {
            const pdf = await DocumentationApi.createPdf(productId);
            window.location.assign(pdf.download.url);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="d-flex flex-column align-items-end gap-1">
            <Button variant="outline-primary" disabled={busy} onClick={download}>
                {busy ? <Spinner size="sm" className="me-2"/> : <i className="bi bi-file-earmark-pdf me-1" aria-hidden="true"></i>}
                {messages.downloadPdf}
            </Button>
            {!!error && (
                <div className="docs-pdf-error">
                    <ErrorAlert error={error} onClose={() => setError(undefined)} messages={{
                        404: messages.nothingToDownload,
                        429: messages.tooManyPdfs
                    }}/>
                </div>
            )}
        </div>
    );
}
